import type { AdminDiaryFilters } from "@/features/admin/diaries/api/adminDiariesApi";
import type { AdminDiariesFilterValues } from "@/features/admin/diaries/components/AdminDiariesFilterMenu";
import { emptyAdminDiariesFilterValues } from "@/features/admin/diaries/components/AdminDiariesFilterMenu";
import { AdminDiariesTable } from "@/features/admin/diaries/components/AdminDiariesTable";
import { AdminDiariesToolbar } from "@/features/admin/diaries/components/AdminDiariesToolbar";
import { isAuditorView } from "@/features/audit/api/auditorApi";
import { AuditorDiarySearch } from "@/features/audit/components/AuditorDiarySearch";
import { LockUnlockDialog } from "@/features/audit/diary-locks/components/LockUnlockDialog";
import { useAuditDiaryList } from "@/features/audit/hooks/useAuditDiaryList";
import { ui } from "@patient-watch/ui";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";

export const Route = createFileRoute("/_app/audit/lock-diaries")({
    component: LockDiariesPage,
});

function LockDiariesPage() {
    // Auditors can see lock status and history but not lock or unlock.
    const auditorView = isAuditorView();
    const [searchText, setSearchText] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [filterValues, setFilterValues] = useState<AdminDiariesFilterValues>(emptyAdminDiariesFilterValues);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

    useEffect(() => {
        const handle = setTimeout(() => setDebouncedSearch(searchText.trim()), 300);
        return () => clearTimeout(handle);
    }, [searchText]);

    const filters: AdminDiaryFilters = useMemo(
        () => ({
            q: debouncedSearch || undefined,
            labels: filterValues.labels.length ? filterValues.labels : undefined,
            clinicians: filterValues.clinicians.length ? filterValues.clinicians : undefined,
            organisations: filterValues.organisations.length ? filterValues.organisations : undefined,
            status: filterValues.status,
            page: 1,
            pageSize: 200,
        }),
        [debouncedSearch, filterValues]
    );

    const { data, isLoading, isFetching, error } = useAuditDiaryList(filters, searchText);
    const diaries = data?.diaries ?? [];

    const selectedDiaries = diaries.filter((d) => selectedIds.has(d.diary_id));

    return (
        <div className="space-y-3">
            <p className="text-muted-foreground text-xs">
                {auditorView
                    ? "Lock status and lock history for the diaries you audit. Locking and unlocking are done by admins and the care team, and both are recorded in the regulated audit trail."
                    : "Sign off (lock) diaries to freeze their clinical data for ALCOA+ regulated capture, or unlock them with a documented reason for controlled corrections. Both actions are recorded in the diary lock history and the regulated audit trail."}
            </p>

            {auditorView ? (
                <AuditorDiarySearch value={searchText} onChange={setSearchText} />
            ) : (
                <AdminDiariesToolbar
                    searchText={searchText}
                    onSearchTextChange={setSearchText}
                    filterValues={filterValues}
                    onApplyFilters={(values) => {
                        setSelectedIds(new Set());
                        setFilterValues(values);
                    }}
                    isApplying={isFetching}
                    showLockStatus
                >
                    <LockUnlockDialog
                        action="lock"
                        selectedDiaries={selectedDiaries}
                        onDone={() => setSelectedIds(new Set())}
                    />
                    <LockUnlockDialog
                        action="unlock"
                        selectedDiaries={selectedDiaries}
                        onDone={() => setSelectedIds(new Set())}
                    />
                </AdminDiariesToolbar>
            )}

            {error && (
                <div className="bg-red-50 p-4 border border-red-200 rounded-md text-red-800 text-sm">
                    Error loading diaries: {error instanceof Error ? error.message : "Unknown error"}
                </div>
            )}

            {isLoading ? (
                <ui.Skeleton className="w-full h-64" />
            ) : (
                !error && (
                    <>
                        <AdminDiariesTable
                            diaries={diaries}
                            selectedIds={selectedIds}
                            onSelectedIdsChange={setSelectedIds}
                            lockColumns
                            selectable={!auditorView}
                        />
                        <p className="text-muted-foreground text-xs">
                            Showing {diaries.length} of {data?.totalCount ?? 0} diaries
                            {(data?.totalCount ?? 0) > diaries.length ? " - refine the filters to narrow the list" : ""}
                            .{" "}
                            {auditorView
                                ? ""
                                : selectedDiaries.length > 0
                                  ? `${selectedDiaries.length} selected.`
                                  : "Tick rows to lock or unlock them."}
                        </p>
                    </>
                )
            )}
        </div>
    );
}
